// Settings page - Data management, export/import, preferences
import { useEffect, useRef, useState } from 'react';
import Header from '../components/Header';
import { getSavedCurves } from '../lib/storage/curves';
import { fixedBondStore, floatingBondStore, SavedFixedRateBond, SavedFloatingRateBond } from '../lib/storage/bonds';
import { getQuoteBook } from '../lib/storage/quoteBook';
import { saveQuoteBook, QuoteBookEntry } from '../lib/storage/quoteBook';
import { indexStore } from '../lib/storage/indices';
import { IrSwapRequest, StoredSwap, getIrSwaps, replaceIrSwaps, clearIrSwaps } from '../lib/storage/swaps';
import { InflationSwapRequest, StoredInflationSwap, getInflationSwaps, replaceInflationSwaps } from '../lib/storage/inflationSwaps';
import { SwaptionRequest, StoredSwaption, getSwaptions, replaceSwaptions, clearSwaptions } from '../lib/storage/swaptions';
import { getSwaptionModels, replaceSwaptionModels, SwaptionModelRecord } from '../lib/storage/swaptionModels';
import { CdsRequest, StoredCds, clearCds, getCdsItems, replaceCds } from '../lib/storage/cds';
import { VolSurfaceSpec, getVolSurfaces } from '../lib/storage/volSurfaces';
import { CreditCurveSpec, getCreditCurves, replaceCreditCurves } from '../lib/storage/creditCurves';
import { EquityOptionRequest, StoredEquityOption, listEquityOptions, replaceEquityOptions } from '../lib/storage/equityOptions';
import {
  getMdBackendSettings,
  getMdBackendStatus,
  saveMdBackendSettings,
  syncLatestSnapshotToLocalQuoteBook,
} from '../lib/marketDataBackend';
import { DataOverviewStats, loadDataOverviewStats } from '../lib/storage/dataOverview';

interface ExportData {
  version: string;
  exportedAt: string;
  indices?: any[];
  curves: any[];
  curveSets?: any[];
  quotes?: any[];
  quoteBook?: QuoteBookEntry[];
  volSurfaces?: VolSurfaceSpec[];
  creditCurves?: CreditCurveSpec[];
  fixedBonds: SavedFixedRateBond[];
  floatingBonds: SavedFloatingRateBond[];
  // The product collections below accept either wrapped entries
  // (preferred) or legacy bare requests, so older backup files keep
  // restoring cleanly while new ones round-trip user-defined names.
  swaps?: Array<IrSwapRequest | StoredSwap>;
  inflationSwaps?: Array<InflationSwapRequest | StoredInflationSwap>;
  swaptions?: Array<SwaptionRequest | StoredSwaption>;
  swaptionModels?: SwaptionModelRecord[];
  cds?: Array<CdsRequest | StoredCds>;
  equityOptions?: Array<EquityOptionRequest | StoredEquityOption>;
}

export default function Settings() {
  const [importing, setImporting] = useState(false);
  const [message, setMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [mdBackendEnabled, setMdBackendEnabled] = useState(false);
  const [mdBackendUrl, setMdBackendUrl] = useState('');
  const [mdBackendStatus, setMdBackendStatus] = useState<string>('Not connected');
  const [mdBackendConnected, setMdBackendConnected] = useState(false);
  const [mdEnabling, setMdEnabling] = useState(false);
  const [mdSyncing, setMdSyncing] = useState(false);

  // Stats
  const [stats, setStats] = useState<DataOverviewStats | null>(null);

  // Load settings and stats on mount
  useEffect(() => {
    const settings = getMdBackendSettings();
    setMdBackendEnabled(settings.enabled);
    setMdBackendUrl(settings.baseUrl);
    if (settings.enabled && settings.baseUrl) {
      void handleEnableMdBackend(settings.baseUrl, true);
    }
    loadStats();
  }, []);

  function getIndices(): any[] {
    try {
      const raw = localStorage.getItem('quantra_indices');
      return raw ? JSON.parse(raw) : [];
    } catch { return []; }
  }

  function getQuotes(): any[] {
    try {
      const raw = localStorage.getItem('quantra_quotes');
      return raw ? JSON.parse(raw) : [];
    } catch { return []; }
  }

  function getCurveSets(): any[] {
    try {
      const raw = localStorage.getItem('quantra_curve_sets');
      return raw ? JSON.parse(raw) : [];
    } catch { return []; }
  }

  async function loadStats() {
    setStats(await loadDataOverviewStats());
  }

  async function handleEnableMdBackend(urlOverride?: string, silent: boolean = false) {
    const targetUrl = (urlOverride ?? mdBackendUrl).trim();
    if (!targetUrl) {
      setMdBackendConnected(false);
      setMdBackendStatus('Connection failed: backend URL is empty');
      if (!silent) setMessage({ type: 'error', text: 'Please provide a backend URL first' });
      return;
    }
    setMdEnabling(true);
    try {
      saveMdBackendSettings({ enabled: true, baseUrl: targetUrl });
      const status = await getMdBackendStatus(targetUrl);
      const activeLabel = status.active ? 'active' : 'inactive';
      const snapshot = status.selected_snapshot_name || 'none';
      const asOf = status.selected_snapshot_as_of || 'n/a';
      setMdBackendEnabled(true);
      setMdBackendConnected(true);
      setMdBackendStatus(`Connected successfully (${activeLabel}) • snapshot=${snapshot} • as_of=${asOf}`);
      if (!silent) setMessage({ type: 'success', text: 'Quantra Market Data enabled and connected' });
    } catch (err) {
      const reason = err instanceof Error ? err.message : 'unknown error';
      setMdBackendEnabled(false);
      setMdBackendConnected(false);
      saveMdBackendSettings({ enabled: false, baseUrl: targetUrl });
      setMdBackendStatus(`Connection failed: ${reason}`);
      if (!silent) setMessage({ type: 'error', text: `Could not enable Quantra Market Data: ${reason}` });
    } finally {
      setMdEnabling(false);
    }
  }

  async function handleSyncMdSnapshot() {
    setMdSyncing(true);
    try {
      const result = await syncLatestSnapshotToLocalQuoteBook(mdBackendUrl);
      await loadStats();
      setMessage({
        type: 'success',
        text:
          `Synced ${result.importedQuotes} quotes from ${result.snapshotName} (${result.snapshotAsOf.slice(0, 10)}). ` +
          'Quotes are now available in Quote Book and Market Data.',
      });
    } catch (err) {
      const reason = err instanceof Error ? err.message : 'unknown error';
      setMessage({ type: 'error', text: `Market data sync failed: ${reason}` });
    } finally {
      setMdSyncing(false);
    }
  }

  async function handleExportAll() {
    try {
      const curves = getSavedCurves();
      const fixedBonds = await fixedBondStore.getAll();
      const floatingBonds = await floatingBondStore.getAll();
      const indices = getIndices();
      const quotes = getQuotes();
      const volSurfaces = getVolSurfaces();
      const creditCurves = getCreditCurves();
      const curveSets = getCurveSets();
      // Export the full wrappers (id + name + request) so saved names
      // round-trip cleanly. Import accepts either shape so old backup
      // files with bare requests still restore correctly.
      const swaps = await getIrSwaps();
      const inflationSwaps = await getInflationSwaps();
      const swaptions = await getSwaptions();
      const swaptionModels = getSwaptionModels();
      const cds = await getCdsItems();
      const equityOptions = await listEquityOptions();

      const exportData: ExportData = {
        version: '1.0',
        exportedAt: new Date().toISOString(),
        indices,
        curves,
        curveSets,
        quotes,
        quoteBook: getQuoteBook(),
        volSurfaces,
        creditCurves,
        fixedBonds,
        floatingBonds,
        swaps,
        inflationSwaps,
        swaptions,
        swaptionModels,
        cds,
        equityOptions,
      };

      const blob = new Blob([JSON.stringify(exportData, null, 2)], { type: 'application/json' });
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `quantra-backup-${new Date().toISOString().split('T')[0]}.json`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);

      setMessage({ type: 'success', text: `Exported ${curves.length} curves, ${curveSets.length} curve sets, ${indices.length} indices, ${quotes.length} quotes, ${volSurfaces.length} vol surfaces, ${creditCurves.length} credit curves, ${fixedBonds.length} fixed bonds, ${floatingBonds.length} floating bonds, ${swaps.length} swaps, ${inflationSwaps.length} inflation swaps, ${swaptions.length} swaptions, ${swaptionModels.length} swaption models, ${cds.length} cds, ${equityOptions.length} equity options` });
    } catch (err) {
      setMessage({ type: 'error', text: 'Failed to export data' });
    }
  }

  async function handleImportAll(file: File, skipConfirm = false, includeMarketData = true) {
    if (!skipConfirm && !confirm('Importing a backup will overwrite existing data. Continue?')) {
      return;
    }
    setImporting(true);
    setMessage(null);

    try {
      const content = await file.text();
      const data = JSON.parse(content) as ExportData;

      let importedCurves = 0;
      let importedIndices = 0;
      let importedQuotes = 0;
      let importedFixedBonds = 0;
      let importedFloatingBonds = 0;
      let importedSwaps = 0;
      let importedInflationSwaps = 0;
      let importedSwaptions = 0;
      let importedSwaptionModels = 0;
      let importedCds = 0;
      let importedEquityOptions = 0;
      let importedVolSurfaces = 0;
      let importedCreditCurves = 0;

      // Import indices
      if (data.indices && Array.isArray(data.indices)) {
        for (const idx of data.indices) {
          if (!idx?.id) continue;
          await indexStore.save(idx as any);
          importedIndices++;
        }
      }

      // Import curves
      if (data.curves && Array.isArray(data.curves)) {
        const existing = getSavedCurves();
        for (const curve of data.curves) {
          if (curve.id && curve.points) {
            const existingIndex = existing.findIndex((c: any) => c.id === curve.id);
            if (existingIndex === -1) {
              existing.push(curve);
            } else {
              existing[existingIndex] = curve;
            }
            importedCurves++;
          }
        }
        localStorage.setItem('quantra_curves', JSON.stringify(existing));
      }

      // Import quotes
      if (includeMarketData && data.quotes && Array.isArray(data.quotes)) {
        let existing = getQuotes();
        for (const quote of data.quotes) {
          if (quote.id) {
            const existingIndex = existing.findIndex((q: any) => q.id === quote.id);
            if (existingIndex === -1) {
              existing.push(quote);
            } else {
              existing[existingIndex] = quote;
            }
            importedQuotes++;
          }
        }
        localStorage.setItem('quantra_quotes', JSON.stringify(existing));
      }

      // Import curve sets
      let importedCurveSets = 0;
      if (data.curveSets && Array.isArray(data.curveSets)) {
        let existingCS = getCurveSets();
        for (const cs of data.curveSets) {
          if (cs.id) {
            const idx = existingCS.findIndex((s: any) => s.id === cs.id);
            if (idx === -1) {
              existingCS.push(cs);
            } else {
              existingCS[idx] = cs;
            }
            importedCurveSets++;
          }
        }
        localStorage.setItem('quantra_curve_sets', JSON.stringify(existingCS));
      }

      // Import fixed bonds
      if (data.fixedBonds && Array.isArray(data.fixedBonds)) {
        for (const bond of data.fixedBonds) {
          if (bond.id) {
            await fixedBondStore.save(bond);
            importedFixedBonds++;
          }
        }
      }

      // Import floating bonds
      if (data.floatingBonds && Array.isArray(data.floatingBonds)) {
        for (const bond of data.floatingBonds) {
          if (bond.id) {
            await floatingBondStore.save(bond);
            importedFloatingBonds++;
          }
        }
      }

      // Import swaps (API-compliant request objects)
      if (data.swaps && Array.isArray(data.swaps)) {
        await replaceIrSwaps(data.swaps);
        importedSwaps = data.swaps.length;
      }

      // Import inflation swaps (API-compliant request objects)
      if (data.inflationSwaps && Array.isArray(data.inflationSwaps)) {
        await replaceInflationSwaps(data.inflationSwaps);
        importedInflationSwaps = data.inflationSwaps.length;
      }

      // Import swaptions (API-compliant request objects)
      if (data.swaptions && Array.isArray(data.swaptions)) {
        await replaceSwaptions(data.swaptions);
        importedSwaptions = data.swaptions.length;
      }

      // Import saved swaption models
      if (data.swaptionModels && Array.isArray(data.swaptionModels)) {
        replaceSwaptionModels(data.swaptionModels);
        importedSwaptionModels = data.swaptionModels.length;
      }

      // Import CDS requests
      if (data.cds && Array.isArray(data.cds)) {
        await replaceCds(data.cds);
        importedCds = data.cds.length;
      }
      if (data.equityOptions && Array.isArray(data.equityOptions)) {
        await replaceEquityOptions(data.equityOptions);
        importedEquityOptions = data.equityOptions.length;
      }

      // Import quoteBook
      let importedQuoteBook = 0;
      if (includeMarketData && data.quoteBook && Array.isArray(data.quoteBook)) {
        const existingBook = getQuoteBook();
        for (const entry of data.quoteBook) {
          if (entry.id && Array.isArray(entry.series)) {
            const idx = existingBook.findIndex(e => e.id === entry.id);
            if (idx === -1) {
              existingBook.push(entry);
            } else {
              existingBook[idx] = entry;
            }
            importedQuoteBook++;
          }
        }
        saveQuoteBook(existingBook);
      }

      if (includeMarketData && data.volSurfaces && Array.isArray(data.volSurfaces)) {
        localStorage.setItem('quantra_vol_surfaces', JSON.stringify(data.volSurfaces));
        importedVolSurfaces = data.volSurfaces.length;
      }
      if (data.creditCurves && Array.isArray(data.creditCurves)) {
        await replaceCreditCurves(data.creditCurves);
        importedCreditCurves = data.creditCurves.length;
      }

      await loadStats();
      setMessage({
        type: 'success',
        text: `Imported ${importedCurves} curves, ${importedCurveSets} curve sets, ${importedIndices} indices, ${importedQuotes} quotes, ${importedQuoteBook} quote book entries, ${importedVolSurfaces} vol surfaces, ${importedCreditCurves} credit curves, ${importedFixedBonds} fixed bonds, ${importedFloatingBonds} floating bonds, ${importedSwaps} swaps, ${importedInflationSwaps} inflation swaps, ${importedSwaptions} swaptions, ${importedSwaptionModels} swaption models, ${importedCds} cds, ${importedEquityOptions} equity options`
      });
    } catch (err) {
      setMessage({ type: 'error', text: 'Failed to import data. Make sure it\'s a valid Quantra backup file.' });
    } finally {
      setImporting(false);
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  }

  async function handleLoadFullExample() {
    if (!confirm('Load full example (portfolio + market data)? This will overwrite existing data.')) {
      return;
    }
    try {
      const backupResponse = await fetch('/example-backup.json');
      if (!backupResponse.ok) throw new Error('Failed to load example backup');
      const backupBlob = await backupResponse.blob();
      const backupFile = new File([backupBlob], 'example-backup.json', { type: 'application/json' });

      // Load portfolio/structures first
      await handleImportAll(backupFile, true, false);

      const marketResponse = await fetch('/example-market-data.json');
      if (!marketResponse.ok) throw new Error('Failed to load example market data');
      const marketBlob = await marketResponse.blob();
      const marketFile = new File([marketBlob], 'example-market-data.json', { type: 'application/json' });

      // Then load quote book + quotes + vols
      await handleImportAll(marketFile, true, true);
      setMessage({ type: 'success', text: 'Loaded full example (portfolio + market data)' });
    } catch {
      setMessage({ type: 'error', text: 'Failed to load full example' });
    }
  }

  async function handleClearAll() {
    if (!confirm('Are you sure you want to delete ALL data? This cannot be undone.')) {
      return;
    }

    try {
      // Clear ALL quantra_* localStorage keys
      const keysToRemove: string[] = [];
      for (let i = 0; i < localStorage.length; i++) {
        const key = localStorage.key(i);
        if (key && key.startsWith('quantra_')) {
          keysToRemove.push(key);
        }
      }

      for (const key of keysToRemove) {
        localStorage.removeItem(key);
      }

      // Also clear via stores (in case they use different key patterns)
      await fixedBondStore.clear();
      await floatingBondStore.clear();
      await clearIrSwaps();
      await clearSwaptions();
      await clearCds();

      await loadStats();
      setMessage({ type: 'success', text: `Cleared all data (${keysToRemove.length} storage keys removed)` });
    } catch (err) {
      setMessage({ type: 'error', text: 'Failed to clear data' });
    }
  }

  return (
    <div className="min-h-screen bg-[#fafafa]">
      <Header />

      <main className="max-w-3xl mx-auto px-4 sm:px-6 pt-24 pb-12">
        <h1 className="text-2xl font-semibold text-[#0a0a0a] mb-2">Settings</h1>
        <p className="text-[#737373] mb-8">Manage your data, preferences, and account</p>

        {/* Message */}
        {message && (
          <div className={`mb-6 p-4 rounded-lg ${message.type === 'success'
              ? 'bg-green-50 border border-green-200 text-green-700'
              : 'bg-red-50 border border-red-200 text-red-700'
            }`}>
            <p className="text-sm">{message.text}</p>
          </div>
        )}

        {/* Data Overview */}
        <section className="bg-white border border-[#e5e5e5] rounded-xl p-6 mb-6">
          <h2 className="text-lg font-semibold text-[#0a0a0a] mb-4">Data Overview</h2>

          {stats && (
            <div className="overflow-hidden border border-[#e5e5e5] rounded-lg mb-6">
              <table className="w-full text-sm">
                <thead className="bg-[#fafafa] border-b border-[#e5e5e5]">
                  <tr>
                    <th className="text-left px-3 py-2 text-xs font-semibold text-[#525252] uppercase tracking-wide">Entity</th>
                    <th className="text-right px-3 py-2 text-xs font-semibold text-[#525252] uppercase tracking-wide">Count</th>
                  </tr>
                </thead>
                <tbody>
                  {[
                    ['Indices', stats.indices],
                    ['Curves', stats.curves],
                    ['Curve Sets', stats.curveSets],
                    ['Quotes', stats.quotes],
                    ['Quote Book Entries', stats.quoteBookEntries],
                    ['Vol Surfaces', stats.volSurfaces],
                    ['Credit Curves', stats.creditCurves],
                    ['Fixed Bonds', stats.fixedBonds],
                    ['Floating Bonds', stats.floatingBonds],
                    ['Swaps', stats.swaps],
                    ['Inflation Swaps', stats.inflationSwaps],
                    ['Swaptions', stats.swaptions],
                    ['Swaption Models', stats.swaptionModels],
                    ['CDS', stats.cds],
                    ['Equity Options', stats.equityOptions],
                    ['Total', stats.total],
                  ].map(([label, value]) => (
                    <tr key={String(label)} className="border-t border-[#f0f0f0]">
                      <td className="px-3 py-1.5 text-[#525252]">{label}</td>
                      <td className="px-3 py-1.5 text-right font-medium text-[#0a0a0a]">{value as number}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          <p className="text-sm text-[#737373]">
            Data is currently stored in your browser's local storage. It will persist across sessions but is not synced to other devices.
          </p>
        </section>

        {/* Quantra Market Data */}
        <section className="bg-white border border-[#e5e5e5] rounded-xl p-6 mb-6">
          <h2 className="text-lg font-semibold text-[#0a0a0a] mb-4">Quantra Market Data</h2>
          <div className="space-y-4">
            <div className="flex items-center justify-between p-4 bg-[#f5f5f5] rounded-lg">
              <div>
                <p className="font-medium text-[#0a0a0a]">Enable Public Market Data Feed</p>
                <p className="text-sm text-[#737373]">
                  When enabled, you can sync live backend snapshots into local Quote Book and Quotes.
                </p>
              </div>
            </div>

            <div className="p-4 bg-[#f5f5f5] rounded-lg">
              <label className="block text-sm font-medium text-[#0a0a0a] mb-2">Backend URL</label>
              <div className="flex items-center gap-2">
                <input
                  type="text"
                  value={mdBackendUrl}
                  onChange={e => setMdBackendUrl(e.target.value)}
                  placeholder="https://market.quantra.io"
                  className="flex-1 px-3 py-2 text-sm border border-[#d4d4d4] rounded-lg focus:outline-none focus:border-[#8a6a2f]"
                />
                <button
                  onClick={() => handleEnableMdBackend()}
                  disabled={mdEnabling || mdSyncing}
                  className="px-4 py-2 text-sm font-medium text-white bg-[#0a0a0a] rounded-lg hover:bg-[#262626]"
                >
                  {mdEnabling ? 'Enabling...' : 'Enable'}
                </button>
                <span
                  className={`inline-flex items-center rounded-full px-3 py-1 text-xs font-medium ${mdBackendConnected
                      ? 'bg-green-100 text-green-700'
                      : 'bg-[#e5e5e5] text-[#525252]'
                    }`}
                >
                  {mdBackendConnected ? 'Connected' : 'Disconnected'}
                </span>
              </div>
              <p className="mt-2 text-xs text-[#737373]">{mdBackendStatus}</p>
            </div>

            <div className="flex items-center justify-between p-4 bg-[#f5f5f5] rounded-lg">
              <div>
                <p className="font-medium text-[#0a0a0a]">Sync Latest Snapshot</p>
                <p className="text-sm text-[#737373]">
                  Imports backend snapshot quotes into local Quote Book time series (as-of date) and flat quote registry.
                </p>
              </div>
              <button
                onClick={handleSyncMdSnapshot}
                disabled={!mdBackendEnabled || !mdBackendConnected || mdSyncing || mdEnabling}
                className={`px-4 py-2 text-sm font-medium rounded-lg transition-colors ${!mdBackendEnabled || !mdBackendConnected || mdSyncing || mdEnabling
                    ? 'text-[#a3a3a3] bg-[#e5e5e5] cursor-not-allowed'
                    : 'text-white bg-[#0a0a0a] hover:bg-[#262626]'
                  }`}
              >
                {mdSyncing ? 'Syncing...' : 'Sync Now'}
              </button>
            </div>
          </div>
        </section>

        {/* Export & Import */}
        <section className="bg-white border border-[#e5e5e5] rounded-xl p-6 mb-6">
          <h2 className="text-lg font-semibold text-[#0a0a0a] mb-4">Export & Import</h2>

          <div className="space-y-4">
            {/* Export All */}
            <div className="flex items-center justify-between p-4 bg-[#f5f5f5] rounded-lg">
              <div>
                <p className="font-medium text-[#0a0a0a]">Download Everything</p>
                <p className="text-sm text-[#737373]">Export all curves, indices, quotes, and bonds as a JSON backup file</p>
              </div>
              <button
                onClick={handleExportAll}
                className="px-4 py-2 text-sm font-medium text-white bg-[#0a0a0a] rounded-lg hover:bg-[#262626] transition-colors flex items-center gap-2"
              >
                <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
                </svg>
                Export
              </button>
            </div>

            {/* Import */}
            <div className="flex items-center justify-between p-4 bg-[#f5f5f5] rounded-lg">
              <div>
                <p className="font-medium text-[#0a0a0a]">Import Backup</p>
                <p className="text-sm text-[#737373]">Restore data from a Quantra backup file</p>
              </div>
              <div>
                <input
                  ref={fileInputRef}
                  type="file"
                  accept=".json"
                  onChange={e => e.target.files?.[0] && handleImportAll(e.target.files[0])}
                  className="hidden"
                  id="import-file"
                />
                <label
                  htmlFor="import-file"
                  className={`px-4 py-2 text-sm font-medium text-[#525252] bg-white border border-[#d4d4d4] rounded-lg hover:bg-[#f5f5f5] transition-colors cursor-pointer flex items-center gap-2 ${importing ? 'opacity-50 pointer-events-none' : ''}`}
                >
                  <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-8l-4-4m0 0L8 8m4-4v12" />
                  </svg>
                  {importing ? 'Importing...' : 'Import'}
                </label>
              </div>
            </div>

            {/* Load full example */}
            <div className="flex items-center justify-between p-4 bg-[#f5f5f5] rounded-lg">
              <div>
                <p className="font-medium text-[#0a0a0a]">Load Full Example</p>
                <p className="text-sm text-[#737373]">Import portfolio + market data in one click</p>
              </div>
              <button
                onClick={handleLoadFullExample}
                className="px-4 py-2 text-sm font-medium text-white bg-[#0a0a0a] rounded-lg hover:bg-[#262626] transition-colors flex items-center gap-2"
              >
                Load Full Example
              </button>
            </div>
          </div>
        </section>

        {/* Danger Zone */}
        <section className="bg-white border border-red-200 rounded-xl p-6">
          <h2 className="text-lg font-semibold text-red-600 mb-4">Danger Zone</h2>

          <div className="flex items-center justify-between p-4 bg-red-50 rounded-lg">
            <div>
              <p className="font-medium text-red-700">Clear All Data</p>
              <p className="text-sm text-red-600">Permanently delete all curves, indices, quotes, and bonds. This cannot be undone.</p>
            </div>
            <button
              onClick={handleClearAll}
              className="px-4 py-2 text-sm font-medium text-white bg-red-600 rounded-lg hover:bg-red-700 transition-colors"
            >
              Clear All
            </button>
          </div>
        </section>

        {/* Storage Info */}
        <section className="mt-6 text-center">
          <p className="text-xs text-[#a3a3a3]">
            Storage: localStorage • Version 1.0 • Future: Firestore sync
          </p>
        </section>
      </main>
    </div>
  );
}
